/**
 * Google Picker API lazy loader and helpers
 *
 * This module provides utilities for loading the Google Picker API on demand
 * and creating file picker instances for Google Drive file selection.
 */

import { getGoogleClientId, getGooglePickerApiKey } from './config';

const GOOGLE_API_SCRIPT_URL = 'https://apis.google.com/js/api.js';

let gapiLoaded = false;
let pickerLoaded = false;
let loadingPromise: Promise<void> | null = null;

/**
 * Load a script dynamically by URL
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Load the gapi.picker module
 */
function loadPickerModule(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!window.gapi) {
      reject(new Error('Google API not loaded'));
      return;
    }

    window.gapi.load('picker', {
      callback: () => {
        pickerLoaded = true;
        resolve();
      },
      onerror: () => reject(new Error('Failed to load picker module')),
    });
  });
}

/**
 * Lazily load Google APIs required for the file picker.
 * This loads the main gapi script and the picker module.
 *
 * @returns Promise that resolves when APIs are loaded
 */
export async function loadGoogleApis(): Promise<void> {
  // Return existing promise if loading is in progress
  if (loadingPromise) {
    return loadingPromise;
  }

  // Skip if already loaded
  if (gapiLoaded && pickerLoaded) {
    return Promise.resolve();
  }

  loadingPromise = (async () => {
    // Load the main Google API script
    if (!window.gapi) {
      await loadScript(GOOGLE_API_SCRIPT_URL);
      gapiLoaded = true;
    }

    // Load the picker module
    if (!pickerLoaded) {
      await loadPickerModule();
    }
  })();

  try {
    await loadingPromise;
  } finally {
    loadingPromise = null;
  }
}

/**
 * Options for creating a Google Picker
 */
export interface CreatePickerOptions {
  /** OAuth access token for the user */
  oauthToken: string;
  /** Callback when files are selected or picker is cancelled */
  callback: GooglePicker.PickerCallback;
  /** Enable multi-select (default: true) */
  multiSelect?: boolean;
  /** MIME types to filter (comma-separated string) */
  mimeTypes?: string;
  /** Custom title for the picker */
  title?: string;
  /** Origin for postMessage (defaults to window.location.origin) */
  origin?: string;
}

/**
 * Create a Google Picker instance for file selection.
 *
 * @param options - Configuration options for the picker
 * @returns The picker instance, or null if configuration is missing
 */
export function createPicker(
  options: CreatePickerOptions
): GooglePicker.Picker | null {
  const apiKey = getGooglePickerApiKey();
  const clientId = getGoogleClientId();

  if (!apiKey) {
    console.error('Google Picker API key not configured');
    return null;
  }

  if (!window.google?.picker) {
    console.error('Google Picker API not loaded. Call loadGoogleApis() first.');
    return null;
  }

  const {
    oauthToken,
    callback,
    multiSelect = true,
    mimeTypes,
    title = 'Select files from Google Drive',
    origin = window.location.origin,
  } = options;

  // Create a DocsView for browsing Google Drive files
  const docsView = new window.google.picker.DocsView(
    window.google.picker.ViewId.DOCS
  );
  docsView.setIncludeFolders(true);

  if (mimeTypes) {
    docsView.setMimeTypes(mimeTypes);
  }

  // Build the picker
  const builder = new window.google.picker.PickerBuilder()
    .addView(docsView)
    .setOAuthToken(oauthToken)
    .setDeveloperKey(apiKey)
    .setCallback(callback)
    .setOrigin(origin)
    .setTitle(title);

  // Set app ID from client ID (format: client_id.apps.googleusercontent.com)
  if (clientId) {
    const appId = clientId.split('.')[0];
    builder.setAppId(appId);
  }

  // Enable multi-select if requested
  if (multiSelect) {
    builder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
  }

  // Enable shared drives support
  builder.enableFeature(window.google.picker.Feature.SUPPORT_DRIVES);

  return builder.build();
}

/**
 * Check if Google Picker APIs are loaded and ready
 */
export function isPickerReady(): boolean {
  return gapiLoaded && pickerLoaded && !!window.google?.picker;
}
